import { useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import Card from "components/card";
import ExpandableDataTable, { DetailGrid, DetailItem } from "components/admin/ExpandableDataTable";
import InputField from "components/fields/InputField";
import ConfirmDialog from "components/admin/ConfirmDialog";
import StatusBadge from "components/admin/StatusBadge";
import usePaginatedResource from "hooks/usePaginatedResource";
import { apiPost, apiDelete } from "lib/apiClient";
import { useAuth } from "contexts/AuthContext";

const columnHelper = createColumnHelper();

const exportColumns = [
  { header: "User ID", accessorKey: "userId" },
  { header: "Title", accessorKey: "title" },
  { header: "Primary contact", accessorKey: "isPrimaryContact" },
  { header: "Membership ID", accessorKey: "id" },
];

export default function UsersPage() {
  const { hasPermission, user: me } = useAuth();
  const canManage = hasPermission("client-portal.manage");

  const { items, loading, error, refetch, exportAll } = usePaginatedResource("/portal/users", { pageSize: 100, itemsKey: "users" });

  const [invite, setInvite] = useState({ name: "", email: "", phone: "", title: "", role: "VIEWER" });
  const [addBusy, setAddBusy] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addNotice, setAddNotice] = useState(null);

  const [pendingRemove, setPendingRemove] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState(null);

  async function addUser() {
    setAddError(null);
    setAddNotice(null);
    if (invite.name.trim().length < 2) return setAddError("Enter their name.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.email.trim())) return setAddError("Enter a valid email address.");
    setAddBusy(true);
    try {
      const result = await apiPost("/portal/users/invite", {
        name: invite.name.trim(),
        email: invite.email.trim(),
        phone: invite.phone.trim() || null,
        title: invite.title.trim() || null,
        role: invite.role,
      });
      setAddNotice(
        result.createdUser
          ? `${invite.email.trim()} has been emailed a welcome with a temporary password. They will choose their own at first sign-in.`
          : `${invite.email.trim()} already had a Wasaa Markets account and can now sign in to this portal with it.`,
      );
      setInvite({ name: "", email: "", phone: "", title: "", role: "VIEWER" });
      refetch();
    } catch (err) {
      setAddError(err.message || "Could not add that person.");
    } finally {
      setAddBusy(false);
    }
  }

  async function confirmRemove() {
    setRemoveBusy(true);
    setRemoveError(null);
    try {
      await apiDelete(`/portal/users/${pendingRemove.userId}`);
      setPendingRemove(null);
      refetch();
    } catch (err) {
      setRemoveError(err.message || "Failed to remove team member.");
    } finally {
      setRemoveBusy(false);
    }
  }

  const columns = [
    columnHelper.accessor("userId", {
      header: "User",
      cell: (info) => {
        const row = info.row.original;
        // Identify a teammate by who they are; the account id is the fallback only when
        // the account genuinely carries no email, phone or handle.
        const label = row.email || row.username || row.phone;
        return (
          <div className="flex items-center gap-2">
            {label ? (
              <p className="text-sm font-semibold text-navy-700 dark:text-white">{label}</p>
            ) : (
              <p className="font-mono text-xs font-semibold text-navy-700 dark:text-white">{info.getValue()}</p>
            )}
            {me?.id === info.getValue() && <StatusBadge label="You" tone="blue" />}
          </div>
        );
      },
    }),
    columnHelper.accessor("title", {
      header: "Title",
      cell: (info) => <p className="text-sm text-navy-700 dark:text-white">{info.getValue() || "—"}</p>,
      meta: { hideBelow: "sm" },
    }),
    columnHelper.accessor("isPrimaryContact", {
      header: "Role",
      cell: (info) =>
        info.getValue() ? <StatusBadge label="Primary contact (admin)" tone="green" /> : <StatusBadge label="Team member" tone="gray" />,
    }),
  ];

  return (
    <div>
      {canManage && (
        <Card extra="p-5 mb-5">
          <p className="text-lg font-semibold text-navy-700 dark:text-white">Add a team member</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            They get an email with a link to this portal and a temporary password. Administrators can approve markets and manage partners; viewers can see everything but change nothing.
          </p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
            A market must be approved by someone other than the person who created it, so you need at least two administrators to approve your own markets.
          </p>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <InputField label="Full name" placeholder="e.g. Tom Otieno" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
            <InputField label="Email" placeholder="name@yourcompany.com" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
            <InputField label="Phone (optional)" placeholder="07XX XXX XXX" value={invite.phone} onChange={(e) => setInvite({ ...invite, phone: e.target.value })} />
            <InputField label="Title (optional)" placeholder="e.g. Finance Manager" value={invite.title} onChange={(e) => setInvite({ ...invite, title: e.target.value })} />
            <div>
              <label htmlFor="invite-role" className="ml-3 text-sm font-semibold text-navy-700 dark:text-white">
                Role
              </label>
              <select
                id="invite-role"
                value={invite.role}
                onChange={(e) => setInvite({ ...invite, role: e.target.value })}
                className="mt-2 flex h-12 w-full items-center rounded-sm border border-gray-200 bg-white/0 px-3 text-sm outline-none dark:!border-white/10 dark:bg-navy-800 dark:text-white"
              >
                <option value="ADMIN">Administrator (approves markets, manages partners)</option>
                <option value="VIEWER">Viewer (read only)</option>
              </select>
            </div>
          </div>
          {addError && <p className="mt-2 text-sm font-medium text-red-500">{addError}</p>}
          {addNotice && <p className="mt-2 text-sm font-medium text-green-600">{addNotice}</p>}
          <button
            type="button"
            onClick={addUser}
            disabled={addBusy}
            className="mt-4 rounded-sm bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            {addBusy ? "Sending invite…" : "Send invite"}
          </button>
        </Card>
      )}

      <ExpandableDataTable
        title="Team"
        subtitle="Everyone with access to this portal for your organization."
        columns={columns}
        exportColumns={exportColumns}
        data={items}
        loading={loading}
        error={error}
        onRetry={refetch}
        emptyMessage="No team members yet."
        exportFilename="team"
        onExportAll={exportAll}
        getRowId={(row) => row.id || row.userId}
        rowLabel={(row) => `team member ${row.userId}`}
        renderDetail={(row) => (
          <DetailGrid>
            <DetailItem label="User ID" mono>
              {row.userId}
            </DetailItem>
            <DetailItem label="Title">{row.title}</DetailItem>
            <DetailItem label="Role">{row.isPrimaryContact ? "Primary contact (full admin access to this portal)" : "Team member"}</DetailItem>
            <DetailItem label="Membership ID" mono>
              {row.id}
            </DetailItem>
            <DetailItem label="Organization" mono>
              {row.clientId}
            </DetailItem>
          </DetailGrid>
        )}
        renderActions={
          canManage
            ? (row) =>
                row.isPrimaryContact ? null : (
                <button
                  type="button"
                  onClick={() => setPendingRemove({ userId: row.userId })}
                  className="rounded-sm bg-red-50 px-3 py-2 text-xs font-semibold text-red-500"
                >
                  Remove from team
                </button>
              )
            : undefined
        }
      />

      <ConfirmDialog
        open={!!pendingRemove}
        title="Remove this team member?"
        description="They will immediately lose access to the portal."
        confirmLabel="Remove"
        tone="danger"
        busy={removeBusy}
        error={removeError}
        onConfirm={confirmRemove}
        onCancel={() => {
          setPendingRemove(null);
          setRemoveError(null);
        }}
      />
    </div>
  );
}
